import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { clearWorkoutItems } from '../ducks/calendarActions';

const ModalContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ModalTitle = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 8px;
`;

const ModalDescription = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  color: #9E9E9E;
  margin-bottom: 20px;
`;

const StyledLink = styled('span')`
  color: #007FAE;
  text-decoration: underline;
  cursor: pointer;
`;

const DateContainer = styled('div')`
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
  margin-bottom: 16px;
`;

const DateLabel = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 600;
`;

const DatePickerContainer = styled('div')`
  width: 48%;
`;

const DatePickerWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  margin-top: 8px;

  .react-datepicker {
    border: none;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
    font-family: 'Nunito Sans';
    width: 320px;
  }

  .react-datepicker__header {
    background: white;
    border-bottom: none;
    padding: 15px;
  }

  .react-datepicker__month-container {
    padding: 15px;
    font-family: 'Nunito Sans';
    font-size: 15px;
  }

  .react-datepicker__day-names,
  .react-datepicker__week {
    margin-bottom: 15px;
  }

  .react-datepicker__day--outside-month {
    color: #BDBDBD;
  }

  .react-datepicker__day--inside-month {
    color: #444444;
  }

  .react-datepicker__day--selected {
    background-color: #FF6600;
    color: white;
  }

  .react-datepicker__day--keyboard-selected {
    background: none;
    color: #444444;
  }

  .react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle,
  .react-datepicker-popper[data-placement^="bottom"] .react-datepicker__triangle::before {
    border-bottom-color: white;
  }

  .react-datepicker-popper {
    z-index: 1;
  }
`;

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
  height: 40px;
  border: 1px solid #E0E0E0;
  border-radius: 4px;
  padding: 0 12px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  background: white;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: #BDBDBD;
  }

  &:focus {
    outline: none;
    border-color: #BDBDBD;
  }
`;

const CheckboxContainer = styled('div')`
  display: flex;
  align-items: center;
  margin-top: 10px;
  margin-bottom: 20px;
  cursor: pointer;
`;

const StyledCheckbox = styled('input')`
  margin-right: 10px;
  cursor: pointer;
  width: 16px;
  height: 16px;
`;

const CheckboxLabel = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  cursor: pointer;
`;

const ButtonsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  margin-top: 20px;
`;

const ClearButton = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  height: 40px;
  background: #FF0505;
  color: white;
  border: none;
  border-radius: 4px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #D32F2F;
  }
`;

const CancelButton = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  height: 40px;
  background: white;
  color: #444444;
  border: 1px solid #444444;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #F5F5F5;
  }
`;

const ClearWorkoutItemsModal = ({ closeModal, modalData }) => {
  const dispatch = useDispatch();
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const currentUser = useSelector((state) => state.auth.data.currentUser);

  // Use modalData.date if provided (from week view), otherwise use selectedDate from Redux
  const initialDate = modalData?.date ? new Date(modalData.date) : new Date(selectedDate);

  const [startDate, setStartDate] = useState(initialDate);
  const [endDate, setEndDate] = useState(initialDate);
  const [includeParentCalendars, setIncludeParentCalendars] = useState(false);

  const handleClearItems = () => {
    const formattedStartDate = moment(startDate).format('YYYY-MM-DD');
    const formattedEndDate = moment(endDate).format('YYYY-MM-DD');
    const submissionObject = {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      clearParents: includeParentCalendars,
      clearWorkoutInfo: false, // By default, we're not clearing workout info
    };
    dispatch(clearWorkoutItems(
      currentUser.accountCode,
      submissionObject,
    ));
    closeModal();
  };

  const handleLearnMoreClick = () => {
    window.open('https://support.teambuildr.com', '_blank');
  };

  return (
    <ModalContainer>
      <ModalTitle>Clear Workout Items</ModalTitle>
      <ModalDescription>
        Recurring workout items cannot be cleared using this method.
        {' '}
        <StyledLink onClick={handleLearnMoreClick}>Click here to learn more.</StyledLink>
      </ModalDescription>

      <DateContainer>
        <DatePickerContainer>
          <DateLabel>Start Date</DateLabel>
          <DatePickerWrapper>
            <StyledDatePicker
              selected={startDate}
              onChange={setStartDate}
              dateFormat='MMMM d, yyyy'
            />
          </DatePickerWrapper>
        </DatePickerContainer>

        <DatePickerContainer>
          <DateLabel>End Date</DateLabel>
          <DatePickerWrapper>
            <StyledDatePicker
              selected={endDate}
              onChange={setEndDate}
              dateFormat='MMMM d, yyyy'
              minDate={startDate}
            />
          </DatePickerWrapper>
        </DatePickerContainer>
      </DateContainer>

      <CheckboxContainer onClick={() => setIncludeParentCalendars(!includeParentCalendars)}>
        <StyledCheckbox
          type='checkbox'
          checked={includeParentCalendars}
          onChange={() => setIncludeParentCalendars(!includeParentCalendars)}
        />
        <CheckboxLabel>Clear items originating from parent calendars</CheckboxLabel>
      </CheckboxContainer>

      <ButtonsContainer>
        <ClearButton onClick={handleClearItems}>CLEAR WORKOUT ITEMS</ClearButton>
        <CancelButton onClick={closeModal}>CANCEL</CancelButton>
      </ButtonsContainer>
    </ModalContainer>
  );
};

ClearWorkoutItemsModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  modalData: PropTypes.shape({
    date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    isFromWeekView: PropTypes.bool,
  }),
};

ClearWorkoutItemsModal.defaultProps = {
  modalData: null,
};

export default ClearWorkoutItemsModal;
